import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { JobService } from '../../services/job/job.service';
import { InspectionServiceKind, PageHeaderReport, ShellService } from '../../../../shell/shell.service';
import { IntakeAccess, IntakeAccessService } from '../../intake-access.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReportManagerComponent } from '../../../popups/component/report-manager/report-manager.component';

// Pages a home inspection does not have (job.job_service 'home'; their APIs refuse it too).
export const TERMITE_PAGES = ['inspections-profile', 'inspections-profile-findings', 'inspections-profile-diagram',
    'inspections-profile-review', 'inspections-job', 'inspections-job-completion', 'inspections-job-review'];

@Component({
    selector: 'app-panel',
    templateUrl: './panel.component.html',
    styleUrls: ['./panel.component.scss']
})
export class PanelComponent implements OnInit, OnDestroy {
    @Input() page_title;
    // Inspection Logs: the address title only, no workflow tab bar (mockup inspection-logs).
    @Input() hideTabs = false;
    public old_site;
    public location = '';
    public jobId = '';
    public allJobsData;
    tab = false;
    showJobsDropdown = false;
    parentChildJobId;
    currentURL = '';
    currentJobIsCancelled = false;
    selectedJob;
    // The job request has answered (or failed): the tab bar can show its tabs.
    jobLoaded = false;
    // HOME_INSPECTIONS (ShellService.homeInspections$); null until known.
    homeInspections: boolean = null;

    constructor(
      private _router: Router,
      private router: ActivatedRoute,
      private jobService: JobService,
      private shell: ShellService,
      private modalService: NgbModal,
      private intakeAccess: IntakeAccessService,
    ) {
        this.currentURL = _router.url;
        const locationHash = _router.url.split('/');
        if (locationHash.length >= 2) {
            this.location = locationHash[1];
        }
        if (locationHash.length === 3) {
            this.jobId = locationHash[2];
            this.tab = false;
        }
    }

    ngOnInit() {
        // Scopes the inspection-page styles in assets/im-ui/forms.css (body.im-inspection).
        document.body.classList.add('im-inspection');
        this.shell.homeInspections$.subscribe(enabled => {
            this.homeInspections = enabled;
            this.updateHeader();
            this.leaveTermitePage();
        });
        this.intakeAccess.access().subscribe(access => {
            this.access = access;
            this.updateHeader();
        });
        this.old_site = environment.apiSite;
        this.updateHeader();

        this.router.params.subscribe(params => {
            if (params.jobId) {
                this.parentChildJobId = this.jobId;
                this.getAllJobs();
                this.showJobsDropdown = true;
            } else {
                this.showJobsDropdown = false;
            }
        });
    }

    ngOnDestroy() {
        document.body.classList.remove('im-inspection');
        this.shell.setPageHeader(null);
    }

    getAllJobs() {
        this.jobService.getAllJobs(this.jobId).subscribe(
            data => {

                this.allJobsData = data.result.data;
                const currentJob = this.allJobsData.find(el => el.job_id == this.jobId);
                this.selectedJob = currentJob;
                if (currentJob) {
                  this.currentJobIsCancelled = currentJob.job_status_top === 'Cancelled';
                }
                this.jobLoaded = true;
                this.updateHeader();
                this.leaveTermitePage();
            },
            () => this.jobLoaded = true
        );
    }

    // Top bar (mockup property-reports): the property address over "Report #1234 RE", the address
    // opening the order's reports newest first. Before a job / property exists: the page title.
    updateHeader() {
        const job = this.selectedJob;
        const hasAddress = !!(job && job.property && job.property.property_address1);
        this.shell.setPageHeader({
            title: hasAddress ? this.addressOf(job) : (this.page_title || 'Inspection').trim(),
            subtitle: job ? 'Report #' + this.reportNumber(job) : '',
            subtitleLink: job ? this.jobLink(this.jobId) : '',
            struck: this.currentJobIsCancelled,
            reports: job ? this.relatedReports() : [],
            // Only while home inspections are on (HOME_INSPECTIONS); off, there is no selector.
            service: job && this.homeInspections ? this.service : null,
            // Intake access (administrators, staff with 'Status Bar - Intake') changes it; others see it.
            serviceChange: job && this.homeInspections && this.access && this.access.intake ? (service => this.changeService(service)) : null
        });
    }

    // ---- Home / Termite Inspection (mockup inspection-service, 2026-10-28) ----

    get service(): InspectionServiceKind {
        return ShellService.serviceOf(this.selectedJob, this.homeInspections);
    }

    private changeService(service: InspectionServiceKind) {
        this.jobService.changeJobService(this.jobId, service).subscribe(() => {
            this.selectedJob.job_service = service;
            this.updateHeader();
            this.leaveTermitePage();
        }, () => this.updateHeader());
    }

    // A home inspection has no termite pages: from one of them, go to its Overview.
    private leaveTermitePage() {
        if (this.service === 'home' && TERMITE_PAGES.indexOf(this.location) !== -1) {
            this._router.navigate(['/inspections-completed-sheet', this.jobId], { replaceUrl: true });
        }
    }

    // A job's Overview, or the first of its pages this user may open (IntakeAccessService).
    private access: IntakeAccess = null;

    private jobLink(jobId): string {
        if (!this.access || this.access.overview) {
            return '/inspections-completed-sheet/' + jobId;
        }
        return this.intakeAccess.jobHome(this.access, jobId) || '';
    }

    private reportNumber(job): string {
        return [job.job_ref_parent, job.job_ref_suffix].filter(v => v).join(' ');
    }

    // The order's parent and child jobs; date = the inspection date set on the Scheduling tab.
    private relatedReports(): PageHeaderReport[] {
        return (this.allJobsData || [])
            .map(job => ({
                id: Number(job.job_id),
                number: this.reportNumber(job),
                type: job.job_sub_type || '',
                date: this.scheduledDate(job.job_date_scheduled),
                link: this.jobLink(job.job_id),
                current: job.job_id == this.jobId,
                cancelled: job.job_status_top === 'Cancelled'
            }))
            .sort((a, b) => ((b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0)) || (b.id - a.id))
            .map(r => ({ number: r.number, type: r.type, date: r.date, link: r.link, current: r.current, cancelled: r.cancelled }));
    }

    private scheduledDate(value: string): Date | null {
        if (!value || value.indexOf('0000-00-00') === 0) {
            return null;
        }
        const date = new Date(value.replace(' ', 'T'));
        return isNaN(date.getTime()) ? null : date;
    }

    openQuickView() {
        const modalRef = this.modalService.open(ReportManagerComponent, { size: 'lg', windowClass: 'im-modal-wide' });
        modalRef.componentInstance.job_id = this.jobId;
        const job = this.selectedJob;
        if (job) {
            const report = 'Report #' + [job.job_ref_parent, job.job_ref_suffix].filter(v => v).join(' ');
            modalRef.componentInstance.context = job.property && job.property.property_address1
                ? this.addressOf(job) + ' · ' + report : report;
        }
    }

    addressOf(job): string {
        const p = job.property || {};
        const cityState = [p.property_city, p.property_state].filter(v => v).join(', ');
        return [p.property_address1, p.property_address2, cityState].filter(v => v).join(', ')
            + (p.property_zip ? ' ' + p.property_zip : '');
    }

    goTotheParentJob() {
        // this._router.navigate([this.location+'/'+this.parentChildJobId]);
        window.location.href = this.location + '/' + this.parentChildJobId;
    }
    goTotheSelectedJob(id) {
        // this._router.navigate([this.location+'/'+this.parentChildJobId]);
        window.location.href = this.location + '/' + id;
    }

    onRightClick(){
        console.log('asdasd');
        return false;
    }
    stopPropagation(event: Event): void {
    event.stopPropagation();
  }

}
